/* eslint-disable camelcase, no-bitwise */
import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { AddOutlined, CheckCircle, ExpandMoreOutlined, HelpOutlineOutlined, HistoryOutlined, TuneOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { rowSx, cardStyle, nodeStyle, doneCheck } from 'src/modules/sending/components/sendingWizardCardStyles';
import { SendingCopyFromMissionModal } from 'src/modules/sending/components/sendingCopyFromMissionModal';
import { SendingCopySelectionModal } from 'src/modules/sending/components/sendingCopySelectionModal';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMembers } from 'src/shared/services/memberService';
import { getAllProfileGroups, getProfileGroupByIdAllItems } from 'src/shared/services/profileGroupService';
import { deriveMembershipYear } from 'src/modules/sending/functions/membershipYear';
import { MemberSearchFilter } from 'src/modules/member/models/memberSearchFilter';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { ProfileItem } from 'src/modules/member/models/profileItem';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { useSendingWizard } from 'src/modules/sending/contexts/sendingWizardContext';

const MAX_DOTS = 200;
// Urvalet baseras alltid på aktiva medlemmar: Betald + Ej betald (aldrig intresseanmälda/utträdda).
const BASE_STATUS = [MemberStatusEnum.Paid, MemberStatusEnum.NotPaid];

export const SendingWizardStep5Selection = () => {
  const isMounted = useIsMounted();
  const { setValue, watch } = useFormContext();
  // Filter-state + cache lever i contexten så de överlever stegnavigering (annars nollställs de vid avmontering).
  const {
    coverage,
    setCoverage,
    selectionTab,
    setSelectionTab,
    dueDateFrom,
    setDueDateFrom,
    dueDateTom,
    setDueDateTom,
    profileFilters,
    setProfileFilters,
    excludeProfileFilters,
    setExcludeProfileFilters,
    dueConfirmed,
    setDueConfirmed,
    includeConfirmed,
    setIncludeConfirmed,
    excludeConfirmed,
    setExcludeConfirmed,
    membershipMMdd,
    gracePeriodDays,
    sendDate,
    cacheRef,
  } = useSendingWizard();
  const ownerId = watch('owner_id') as number | undefined; // faktureringsgrupp (för Kivra-kollen)
  // Samlingsfaktura (default Ja): hushållsmedlemmar slås ihop på huvudmedlemmens faktura → färre fakturor.
  // Styr hur mottagar-/kanal-/kostnadsräkningen görs (nedan) och vilket värde inv_template.householdInvoice får.
  const householdInvoice = watch('householdInvoice') !== false;
  // Avancerat: skapa faktura även för avgiftsfria medlemmar (t.ex. hedersmedlemmar) – makulerad 0-kr-faktura så de
  // omfattas av utskicket i stället för att hoppas över. Styr inv_template.printZeroInvoice. Default av.
  const printZeroInvoice = watch('printZeroInvoice') === true;

  const [total, setTotal] = useState<number | null>(null); // alla medlemmar (bakgrundsmassan)
  const [count, setCount] = useState<number | null>(null); // matchar urvalet
  const [loading, setLoading] = useState(false);

  // Profilväljare
  const [groups, setGroups] = useState<ProfileGroup[]>([]);
  const [items, setItems] = useState<ProfileItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [addTarget, setAddTarget] = useState<'include' | 'exclude' | null>(null); // vilket kort som lägger till profil
  const [selGroup, setSelGroup] = useState<ProfileGroup | null>(null);
  const [selItem, setSelItem] = useState<ProfileItem | null>(null);
  const [compareTarget, setCompareTarget] = useState<'include' | 'exclude' | null>(null); // öppen jämförelsemodal (profiler)
  const [explainOpen, setExplainOpen] = useState(false); // modal "Förklaring" (förfallodatum)
  const [showDetails, setShowDetails] = useState(false); // visa datumfälten (avancerat) i förfallodatum-steget
  const [copySelectionOpen, setCopySelectionOpen] = useState(false); // "Gör som en tidigare avisering"-modal (kopiera hela urvalet)

  // Totala medlemsmassan (Betald + Ej betald) – hämtas en gång.
  useEffect(() => {
    (async () => {
      try {
        const res = await getMembers(0, 1, 'member_id', 'asc', { status: [...BASE_STATUS] }, [], '');
        if (isMounted()) setTotal(res.data.totalItems);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    })();
  }, [isMounted]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getAllProfileGroups(true, true);
        if (isMounted()) setGroups(res.data.results ?? []);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    })();
  }, [isMounted]);

  // Profiler (items) finns inte i grupplistan – hämtas separat vid gruppval (som i utökad sök).
  useEffect(() => {
    if (!selGroup) {
      setItems([]);
      return;
    }
    (async () => {
      setLoadingItems(true);
      try {
        const res = await getProfileGroupByIdAllItems(selGroup.memberProfile_id, true);
        if (isMounted()) setItems(res.data.profileItems ?? []);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoadingItems(false);
      }
    })();
  }, [selGroup, isMounted]);

  // Live-antal: snabba count-frågor för antal medlemmar + antal fakturor. Kanal-täckningen (masks) för leverans- och
  // kostnadsstegen är tung (laddar hem alla rader) och beräknas i Leveransmetod-steget, där det steget hinner slutföras
  // medan det är monterat (se sendingWizardStep4Delivery).
  useEffect(() => {
    // Fritext-läget beräknar inget urval (användaren beskriver själv).
    if (selectionTab === 1) {
      setLoading(false);
      return undefined;
    }

    // Sökningen görs först när alla steg är bekräftade (Klar-knappen på varje kort).
    if (!(dueConfirmed && includeConfirmed && excludeConfirmed)) {
      setCount(null);
      setCoverage(null);
      setLoading(false);
      return undefined;
    }

    const filter: MemberSearchFilter = { status: [...BASE_STATUS] };
    if (dueDateFrom) filter.dueDateStart = dueDateFrom.format('YYYYMMDD');
    if (dueDateTom) filter.dueDateEnd = dueDateTom.format('YYYYMMDD');
    if (profileFilters.length) filter.profileItemIds = profileFilters.map((p) => p.itemId);
    if (excludeProfileFilters.length) filter.excludedProfileItemIds = excludeProfileFilters.map((p) => p.itemId);

    // Nyckel som identifierar urvalet (för cachen).
    const incIds = profileFilters
      .map((p) => p.itemId)
      .sort((a, b) => a - b)
      .join(',');
    const excIds = excludeProfileFilters
      .map((p) => p.itemId)
      .sort((a, b) => a - b)
      .join(',');
    const kivraKey = `${ownerId ?? 0}|${dueDateFrom ? dueDateFrom.format('YYYYMMDD') : ''}|${
      dueDateTom ? dueDateTom.format('YYYYMMDD') : ''
    }|${incIds}|${excIds}|h${householdInvoice ? 1 : 0}`;

    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        // Återanvänd cachen om urvalet är oförändrat – då körs inte count-anropen igen.
        const cached = cacheRef.current;
        if (cached && cached.key === kivraKey) {
          setCount(cached.count);
          setCoverage(cached.coverage);
          setLoading(false);
          return;
        }

        // Snabba antal-frågor (pageSize=1, bara totalItems) – ger antal medlemmar + antal fakturor direkt utan att
        // ladda hem alla medlemsrader. Fakturor (mottagare): vid samlingsfaktura räknar backend-filtret
        // isMainMember=true bort hushållsmedlemmar (underordnade); annars = alla medlemmar.
        const totalRes = await getMembers(0, 1, 'member_id', 'asc', filter, [], '');
        if (!isMounted()) return;
        const { totalItems } = totalRes.data;

        let recipients = totalItems;
        if (householdInvoice) {
          const recRes = await getMembers(0, 1, 'member_id', 'asc', { ...filter, isMainMember: 'true' }, [], '');
          if (!isMounted()) return;
          recipients = recRes.data.totalItems;
        }

        // Visa talen direkt. Kanal-täckning (masks) fylls i av Leveransmetod-steget (tungt anrop där steget är monterat).
        // total = antal fakturor (mottagare), membersCovered = antal personer som omfattas (oberoende av samlingsfaktura).
        const cov = { total: recipients, membersCovered: totalItems, masks: [], approx: false, kivraAvailable: false };
        setCount(totalItems);
        setCoverage(cov);
        cacheRef.current = { key: kivraKey, count: totalItems, breakdown: { approx: false, countries: [] }, coverage: cov };
        setLoading(false);
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setLoading(false);
        }
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [
    selectionTab,
    dueDateFrom,
    dueDateTom,
    profileFilters,
    excludeProfileFilters,
    dueConfirmed,
    includeConfirmed,
    excludeConfirmed,
    ownerId,
    householdInvoice,
    isMounted,
    setCoverage,
    cacheRef,
  ]);

  // Generera en läsbar urvalsbeskrivning (sparas i selectionDescription).
  useEffect(() => {
    // Auto-generera bara i urvals-lägena (0 = eget urval, 2 = kopierat). Fritext (1) styr fältet själv,
    // och -1 = inget läge valt ännu.
    if (selectionTab !== 0 && selectionTab !== 2) return;
    const clauses: string[] = [];
    if (dueDateFrom && dueDateTom) clauses.push(`förfallodatum ${dueDateFrom.format('YYYY-MM-DD')} – ${dueDateTom.format('YYYY-MM-DD')}`);
    else if (dueDateFrom) clauses.push(`förfallodatum från ${dueDateFrom.format('YYYY-MM-DD')}`);
    else if (dueDateTom) clauses.push(`förfallodatum t.o.m. ${dueDateTom.format('YYYY-MM-DD')}`);
    profileFilters.forEach((p) => clauses.push(`profil ${p.groupTitle}: ${p.itemName}`));
    excludeProfileFilters.forEach((p) => clauses.push(`ej profil ${p.groupTitle}: ${p.itemName}`));
    if (!clauses.length) {
      setValue('selectionDescription', '', { shouldValidate: true });
      return;
    }
    const base = `Medlemmar med ${clauses.join('; ')}`;
    const desc = count != null ? `${base} (ca ${count} st)` : base;
    setValue('selectionDescription', desc, { shouldValidate: true });
  }, [selectionTab, dueDateFrom, dueDateTom, profileFilters, excludeProfileFilters, count, setValue]);

  // Ändrar man profilurvalet manuellt är det inte längre "gör som tidigare avisering" – gå till eget urval (läge 0).
  const leaveCopyMode = () => {
    if (selectionTab === 2) setSelectionTab(0);
  };

  const addProfileFilter = () => {
    if (!selGroup || !selItem) return;
    const setter = addTarget === 'exclude' ? setExcludeProfileFilters : setProfileFilters;
    const current = addTarget === 'exclude' ? excludeProfileFilters : profileFilters;
    const exists = current.some((p) => p.itemId === selItem.memberProfileItem_id);
    if (!exists) {
      setter((prev) => [
        ...prev,
        { groupId: selGroup.memberProfile_id, groupTitle: selGroup.title, itemId: selItem.memberProfileItem_id, itemName: selItem.name },
      ]);
      leaveCopyMode();
    }
    setSelGroup(null);
    setSelItem(null);
    setAddTarget(null);
  };

  // Lägg till ALLA profiler i vald grupp på en gång (hoppar över redan tillagda) – spar tid, kryssa sedan bort oönskade.
  const addAllInGroup = () => {
    if (!selGroup) return;
    const setter = addTarget === 'exclude' ? setExcludeProfileFilters : setProfileFilters;
    const current = addTarget === 'exclude' ? excludeProfileFilters : profileFilters;
    const toAdd = items
      .filter((i) => !current.some((p) => p.itemId === i.memberProfileItem_id))
      .map((i) => ({ groupId: selGroup.memberProfile_id, groupTitle: selGroup.title, itemId: i.memberProfileItem_id, itemName: i.name }));
    if (toAdd.length) {
      setter((prev) => [...prev, ...toAdd]);
      leaveCopyMode();
    }
    setSelGroup(null);
    setSelItem(null);
    setAddTarget(null);
  };

  const removeProfileFilter = (itemId: number) => {
    setProfileFilters((prev) => prev.filter((p) => p.itemId !== itemId));
    leaveCopyMode();
  };
  const removeExcludeFilter = (itemId: number) => {
    setExcludeProfileFilters((prev) => prev.filter((p) => p.itemId !== itemId));
    leaveCopyMode();
  };

  // Prickkarta: hela massan (total) som prickar, de valda highlightade. Skalas vid stora register.
  const dotsTotal = total != null ? Math.min(total, MAX_DOTS) : 0;
  const perDot = total != null && total > MAX_DOTS ? Math.ceil(total / MAX_DOTS) : 1;
  let selectedDots = 0;
  if (total && count != null) {
    selectedDots = Math.round((count / total) * dotsTotal);
    if (count > 0 && selectedDots === 0) selectedDots = 1;
    if (selectedDots > dotsTotal) selectedDots = dotsTotal;
  }

  const pre = coverage?.approx ? '≈ ' : '';

  // Steg-status = bekräftade steg (Klar). Resultatet till höger visas först när alla steg är klara.
  const dueDone = dueConfirmed;
  const includeDone = includeConfirmed;
  const excludeDone = excludeConfirmed;
  const hasSelection = dueConfirmed && includeConfirmed && excludeConfirmed;
  // Samlingsfaktura (default Ja): hushållsmedlemmar slås ihop under huvudmedlemmen → färre fakturor (mottagare) än medlemmar.
  // Antal medlemmar (personer) som omfattas resp. antal fakturor (mottagare). coverage.total är redan mottagar-
  // nivå (hushåll ihopslagna vid samlingsfaktura) och används även av leverans- och kostnadsstegen.
  const membersCovered = coverage ? coverage.membersCovered : count;
  const recipients = coverage ? coverage.total : null;
  const hasDueDate = !!(dueDateFrom || dueDateTom);
  const dueSummary = (() => {
    const from = dueDateFrom?.format('YYYY-MM-DD') ?? '';
    const tom = dueDateTom?.format('YYYY-MM-DD') ?? '';
    if (dueDateFrom && dueDateTom && !dueDateFrom.isSame(dueDateTom, 'day')) return `${from} – ${tom}`;
    if (tom && !from) return `t.o.m. ${tom}`; // "Alla" – ingen nedre gräns, tydliggör att det är upp t.o.m. datumet
    if (from && !tom) return `från ${from}`;
    return from || tom;
  })();

  // Medlemsåret HÄRLEDS ur utskicksdatum + betaldatum + friperiod (kan inte längre väljas fritt).
  const expMM = membershipMMdd ? membershipMMdd.slice(0, 2) : '';
  const expDd = membershipMMdd ? membershipMMdd.slice(2, 4) : '';
  const isCalendarYear = membershipMMdd === '1231';
  const membershipYearDate = (dy: number) => `${dy}-${expMM}-${expDd}`;
  // Etikett: kalenderår (1231) → ett årtal (dy+1). Brutet medlemsår (t.ex. 1 sep–31 aug) → delat årtal "dy/dy+1".
  const membershipYearLabel = (dy: number) => (isCalendarYear ? `${dy + 1}` : `${dy}/${dy + 1}`);

  // Härled medlemsåret ur datumen + friperioden (delad logik med utskicksdatum-panelen).
  const payDateObj = watch('payDate') ? dayjs(watch('payDate') as string) : null;
  const derivedResult = deriveMembershipYear({ sendDate, payDate: payDateObj, expireMMdd: membershipMMdd, graceDays: gracePeriodDays });
  // Bas-år för medlemsurvalet (medlemmarnas nuvarande förfallo-år) = härlett expiry-år − 1.
  const selectedDy = derivedResult ? derivedResult.selectionYear : null;
  const dueTomStr = dueDateTom?.format('YYYY-MM-DD') ?? null;

  // Hur långt bakåt obetalda medlemmar tas med i urvalet. dueDateFrom = null → "Alla" (ingen nedre gräns),
  // annars antal år bakom basåret (0 = endast innevarande medlemsår).
  let rangeYearsBack: number | null = null;
  if (dueDateFrom != null) rangeYearsBack = selectedDy != null ? selectedDy - dueDateFrom.year() : 0;
  // Chipsen styr hur långt bakåt obetalda medlemmar tas med. "Nej" = bara innevarande medlemsår (inget tillägg av
  // tidigare år); "Från <år>" = ta även med obetalda med förfallodatum från och med det året (förfallodatum-året, inte
  // medlemsårs-etiketten); "Alla" = ingen nedre gräns.
  const dy = selectedDy ?? 0;
  const dueRangeOptions: { label: string; yearsBack: number | null }[] = [
    { label: 'Nej', yearsBack: 0 },
    { label: `Från ${dy - 1}`, yearsBack: 1 },
    { label: `Från ${dy - 2}`, yearsBack: 2 },
    { label: `Från ${dy - 3}`, yearsBack: 3 },
    { label: 'Alla', yearsBack: null },
  ];
  const setDueRange = (yearsBack: number | null) => {
    if (yearsBack == null) setDueDateFrom(null);
    else if (selectedDy != null) setDueDateFrom(dayjs(membershipYearDate(selectedDy - yearsBack)));
  };
  // Text-suffix som sammanfattar hur långt bakåt obetalda tas med.
  const dueRangeSuffix = (() => {
    if (rangeYearsBack == null) return ' (inkl. alla obetalda)';
    if (rangeYearsBack > 0 && selectedDy != null) return ` (inkl. obetalda från ${selectedDy - rangeYearsBack})`;
    return '';
  })();

  // Sätt urvalsperioden automatiskt till det härledda medlemsåret. Byter året (datumen ändras) återställs perioden.
  // Rör inte om året redan speglas (bevarar "ta med obetalda"-valet vid återöppning/stegnavigering).
  useEffect(() => {
    if (selectedDy == null) return;
    const tom = membershipYearDate(selectedDy);
    if (dueTomStr !== tom) {
      setDueDateTom(dayjs(tom));
      setDueDateFrom(dayjs(tom));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDy]);

  // Kort-/nod-/lås-stilar delas mellan wizard-stegen, se sendingWizardCardStyles.

  // Redan tillagda profiler (för det aktiva kortet) filtreras bort ur profil-listan så de inte går att välja igen.
  const currentAddList = addTarget === 'exclude' ? excludeProfileFilters : profileFilters;
  const availableItems = items.filter((i) => !currentAddList.some((p) => p.itemId === i.memberProfileItem_id));

  // Delat formulär för att lägga till en profil (grupp + profil). Renderas i det kort vars addTarget är aktivt.
  const addForm = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1 }}>
      <Autocomplete
        size='small'
        options={groups}
        value={selGroup}
        onChange={(_e, v) => {
          setSelGroup(v);
          setSelItem(null);
        }}
        getOptionLabel={(g) => g.title}
        isOptionEqualToValue={(a, b) => a.memberProfile_id === b.memberProfile_id}
        renderInput={(params) => <TextField {...params} label='Profilgrupp' />}
        sx={{ minWidth: 220 }}
      />
      <Autocomplete
        size='small'
        options={availableItems}
        value={selItem}
        onChange={(_e, v) => setSelItem(v)}
        getOptionLabel={(i) => i.name}
        isOptionEqualToValue={(a, b) => a.memberProfileItem_id === b.memberProfileItem_id}
        disabled={!selGroup || loadingItems}
        loading={loadingItems}
        noOptionsText='Alla profiler i gruppen är redan tillagda'
        renderInput={(params) => <TextField {...params} label='Profil' />}
        sx={{ minWidth: 220 }}
      />
      <Button variant='contained' disableElevation onClick={addProfileFilter} disabled={!selItem}>
        Lägg till
      </Button>
      <Button variant='outlined' disableElevation onClick={addAllInGroup} disabled={!selGroup || loadingItems || availableItems.length === 0}>
        Lägg till alla i gruppen
      </Button>
      <Button
        color='inherit'
        onClick={() => {
          setAddTarget(null);
          setSelGroup(null);
          setSelItem(null);
        }}
      >
        Avbryt
      </Button>
    </Box>
  );

  return (
    <GridWrapper>
      {/* Vänster: rubrik + lägesknappar + urvalsbyggaren. Fritext-fältet tänds ovanför och byggaren disablas när fritext är vald. */}
      <Grid item xs={12} md={7}>
        {/* Samma vänsterindrag som korten (tidslinjens gutter) så rubrik + lägesknappar linjerar med kortens innehåll. */}
        <Box sx={{ mb: 2, pl: { xs: 6, sm: 7 } }}>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
            Vilka medlemmar ska få en faktura?
          </Typography>
          {/* Tre lägen: kopiera en tidigare aviserings urval, bygg urval själv, eller beskriv i fritext. */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={selectionTab === 2 ? 'contained' : 'outlined'}
              disableElevation
              startIcon={<HistoryOutlined />}
              onClick={() => setCopySelectionOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Gör som en tidigare avisering
            </Button>
            <Button
              variant={selectionTab === 0 ? 'contained' : 'outlined'}
              disableElevation
              onClick={() => setSelectionTab(0)}
              sx={{ textTransform: 'none' }}
            >
              Välj vilka medlemmar
            </Button>
            <Button
              variant={selectionTab === 1 ? 'contained' : 'outlined'}
              disableElevation
              onClick={() => setSelectionTab(1)}
              sx={{ textTransform: 'none' }}
            >
              Förklara med text
            </Button>
          </Box>
        </Box>
        {selectionTab === 1 && (
          <Box sx={{ mb: 3, pl: { xs: 6, sm: 7 } }}>
            <ControlledTextField
              name='selectionDescription'
              required
              multiline
              rows={4}
              label='Beskriv urvalet'
              helperText='Beskriv i fritext vilka medlemmar som ska få en faktura.'
            />
          </Box>
        )}
        {/* Inget läge valt ännu (ingen knapp förvald): uppmana att välja hur urvalet ska göras. */}
        {selectionTab === -1 && (
          <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant='body2'>Välj hur du vill göra urvalet ovan för att börja.</Typography>
          </Box>
        )}
        {/* Urvalsbyggaren visas bara i urvals-lägena (0 = eget urval, 2 = kopierat) – i fritext-läget räcker textfältet ovan. */}
        {selectionTab !== -1 && selectionTab !== 1 && (
          <Box>
            {/* Tidslinje: korten trädda på en gemensam streckad vänsterlinje (ikon-nod per kort). */}
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 20, sm: 24 },
                  top: 0,
                  bottom: 0,
                  borderLeft: '2px dashed',
                  borderColor: 'grey.400',
                  zIndex: 0,
                }}
              />

              {/* Steg: förfallodatum */}
              <Box sx={rowSx}>
                <Box sx={nodeStyle(dueDone)}>
                  {dueDone ? (
                    <CheckCircle sx={{ fontSize: 22 }} />
                  ) : (
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>1</Typography>
                  )}
                </Box>
                <Box sx={cardStyle(dueDone)}>
                  {dueDone && doneCheck}
                  <Typography variant='subtitle1' sx={{ fontWeight: 500, mb: dueDone ? 0.5 : 1 }}>
                    Medlemmar som ska betala för medlemsår{selectedDy != null ? ` ${membershipYearLabel(selectedDy)}` : ''}:
                  </Typography>

                  {dueDone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant='body2'>
                        {membershipMMdd && selectedDy != null ? (
                          <b>
                            Medlemsår {membershipYearLabel(selectedDy)}
                            {dueRangeSuffix}
                          </b>
                        ) : (
                          <>
                            Förfallodatum: <b>{dueSummary}</b>
                          </>
                        )}
                      </Typography>
                      <Button variant='outlined' size='small' onClick={() => setDueConfirmed(false)} sx={{ flexShrink: 0 }}>
                        Ändra
                      </Button>
                    </Box>
                  )}

                  {!dueDone && (
                    <>
                      {derivedResult && selectedDy != null ? (
                        <Box sx={{ mb: 1 }}>
                          {/* Härlett medlemsår (styrs av utskicksdatum + betaldatum + friperiod – inte fritt valbart). */}
                          <Alert severity='info' icon={false} sx={{ mb: 2 }}>
                            Aviseringen gäller <b>medlemsår {membershipYearLabel(selectedDy)}</b>. Medlemsåret styrs av utskicksdatum och sista
                            betaldatum.
                          </Alert>

                          {/* Hur långt bakåt ska obetalda medlemmar tas med? */}
                          <Typography variant='body2' sx={{ mb: 1 }}>
                            Ta även med obetalda medlemmar bakåt i tiden:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {dueRangeOptions.map((opt) => {
                              const active = opt.yearsBack === rangeYearsBack;
                              return (
                                <Chip
                                  key={opt.label}
                                  label={opt.label}
                                  color={active ? 'primary' : 'default'}
                                  variant={active ? 'filled' : 'outlined'}
                                  onClick={() => setDueRange(opt.yearsBack)}
                                />
                              );
                            })}
                          </Box>

                          <Button
                            variant='text'
                            size='small'
                            onClick={() => setShowDetails((v) => !v)}
                            sx={{ mt: 2, p: 0, minWidth: 0, textTransform: 'none' }}
                          >
                            {showDetails ? 'Dölj detaljer' : 'Visa detaljer'}
                          </Button>
                          {showDetails && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
                              <Typography variant='caption' color='text.secondary'>
                                Hämtar medlemmar med förfallodatum {dueSummary}
                              </Typography>
                              <Button
                                variant='text'
                                size='small'
                                startIcon={<HelpOutlineOutlined sx={{ fontSize: 18 }} />}
                                onClick={() => setExplainOpen(true)}
                                sx={{ p: 0, minWidth: 0, textTransform: 'none', flexShrink: 0 }}
                              >
                                Förklaring
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        // Ingen härledning möjlig (utskicksdatum/betaldatum eller förbundskonfig saknas) → sätt perioden manuellt.
                        <Box sx={{ mt: 1 }}>
                          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                            Ange utskicksdatum och sista betaldatum högst upp så räknas medlemsåret ut automatiskt – eller sätt perioden manuellt
                            nedan.
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <DatePicker
                              format='YYYY-MM-DD'
                              label='Från'
                              value={dueDateFrom}
                              onChange={(date) => setDueDateFrom(date)}
                              slotProps={{ textField: { size: 'small', sx: { maxWidth: 180 } } }}
                            />
                            <DatePicker
                              format='YYYY-MM-DD'
                              label='Till'
                              value={dueDateTom}
                              onChange={(date) => setDueDateTom(date)}
                              slotProps={{ textField: { size: 'small', sx: { maxWidth: 180 } } }}
                            />
                            {hasDueDate && (
                              <Button
                                size='small'
                                color='inherit'
                                onClick={() => {
                                  setDueDateFrom(null);
                                  setDueDateTom(null);
                                }}
                              >
                                Rensa
                              </Button>
                            )}
                          </Box>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                          variant='contained'
                          size='medium'
                          disableElevation
                          disabled={!hasDueDate}
                          startIcon={<CheckCircle />}
                          onClick={() => setDueConfirmed(true)}
                        >
                          Klar
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              {/* Steg: profiler att inkludera – visas först när medlemsår-steget är klart (tonas in). */}
              {dueConfirmed && (
                <Fade in appear timeout={350}>
                  <Box sx={rowSx}>
                    <Box sx={nodeStyle(includeDone)}>
                      {includeDone ? (
                        <CheckCircle sx={{ fontSize: 22 }} />
                      ) : (
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>2</Typography>
                      )}
                    </Box>
                    <Box sx={cardStyle(includeDone)}>
                      {includeDone && doneCheck}
                      <Typography variant='subtitle1' sx={{ fontWeight: 500, mb: 1 }}>
                        Ta bara med dem som även har profilerna:
                      </Typography>
                      {profileFilters.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                          {profileFilters.map((p) => (
                            <Chip
                              key={p.itemId}
                              label={`${p.groupTitle}: ${p.itemName}`}
                              onDelete={includeDone ? undefined : () => removeProfileFilter(p.itemId)}
                              color='primary'
                              variant='outlined'
                            />
                          ))}
                        </Box>
                      )}

                      {includeDone && (
                        <>
                          {profileFilters.length === 0 && (
                            <Typography variant='body2' color='text.secondary'>
                              Inget profilvillkor
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button variant='outlined' size='small' onClick={() => setIncludeConfirmed(false)}>
                              Ändra
                            </Button>
                          </Box>
                        </>
                      )}

                      {!includeDone && addTarget === 'include' && addForm}
                      {!includeDone && addTarget !== 'include' && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            <Button startIcon={<AddOutlined />} onClick={() => setAddTarget('include')}>
                              Lägg till profilfilter
                            </Button>
                            <Button
                              variant='contained'
                              size='medium'
                              disableElevation
                              startIcon={profileFilters.length > 0 ? <CheckCircle /> : undefined}
                              onClick={() => setIncludeConfirmed(true)}
                              sx={{ ml: 'auto' }}
                            >
                              {profileFilters.length > 0 ? 'Klar' : 'Hoppa över'}
                            </Button>
                          </Box>
                          <Box sx={{ mt: 1 }}>
                            <Button
                              variant='text'
                              color='inherit'
                              startIcon={<HistoryOutlined />}
                              onClick={() => setCompareTarget('include')}
                              sx={{ color: 'text.secondary' }}
                            >
                              Jämför med tidigare aviseringar
                            </Button>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Fade>
              )}

              {/* Steg: profiler att undanta – visas först när inkludera-steget är klart (tonas in). */}
              {includeConfirmed && (
                <Fade in appear timeout={350}>
                  <Box sx={rowSx}>
                    <Box sx={nodeStyle(excludeDone)}>
                      {excludeDone ? (
                        <CheckCircle sx={{ fontSize: 22 }} />
                      ) : (
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>3</Typography>
                      )}
                    </Box>
                    <Box sx={cardStyle(excludeDone)}>
                      {excludeDone && doneCheck}
                      <Typography variant='subtitle1' sx={{ fontWeight: 500, mb: 1 }}>
                        Undanta dem som har profilerna:
                      </Typography>
                      {excludeProfileFilters.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                          {excludeProfileFilters.map((p) => (
                            <Chip
                              key={p.itemId}
                              label={`${p.groupTitle}: ${p.itemName}`}
                              onDelete={excludeDone ? undefined : () => removeExcludeFilter(p.itemId)}
                              variant='outlined'
                            />
                          ))}
                        </Box>
                      )}

                      {excludeDone && (
                        <>
                          {excludeProfileFilters.length === 0 && (
                            <Typography variant='body2' color='text.secondary'>
                              Inga undantag – ingen utesluts.
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button variant='outlined' size='small' onClick={() => setExcludeConfirmed(false)}>
                              Ändra
                            </Button>
                          </Box>
                        </>
                      )}

                      {!excludeDone && addTarget === 'exclude' && addForm}
                      {!excludeDone && addTarget !== 'exclude' && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            <Button startIcon={<AddOutlined />} onClick={() => setAddTarget('exclude')}>
                              Lägg till profil att undanta
                            </Button>
                            <Button
                              variant='contained'
                              size='medium'
                              disableElevation
                              startIcon={excludeProfileFilters.length > 0 ? <CheckCircle /> : undefined}
                              onClick={() => setExcludeConfirmed(true)}
                              sx={{ ml: 'auto' }}
                            >
                              {excludeProfileFilters.length > 0 ? 'Klar' : 'Hoppa över'}
                            </Button>
                          </Box>
                          <Box sx={{ mt: 1 }}>
                            <Button
                              variant='text'
                              color='inherit'
                              startIcon={<HistoryOutlined />}
                              onClick={() => setCompareTarget('exclude')}
                              sx={{ color: 'text.secondary' }}
                            >
                              Jämför med tidigare aviseringar
                            </Button>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Fade>
              )}
            </Box>
          </Box>
        )}
      </Grid>

      {/* Höger: live-antal + prickkarta + nedbrytning per land – döljs i fritext-läge */}
      {selectionTab !== 1 && selectionTab !== -1 && (
        <Grid item xs={12} md={5}>
          <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', p: 5, height: '100%' }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1.5 }}>
              Ditt urval
            </Typography>
            {!hasSelection ? (
              <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                <TuneOutlined sx={{ fontSize: 44, opacity: 0.4, mb: 1 }} />
                <Typography variant='body2'>Gör dina val till vänster</Typography>
              </Box>
            ) : (
              <>
                {/* Samlingsfaktura: hushållsmedlemmar slås ihop under huvudmedlemmen (en faktura). */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.75 }}>
                    Samlingsfaktura
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label='Ja'
                      color={householdInvoice ? 'primary' : 'default'}
                      variant={householdInvoice ? 'filled' : 'outlined'}
                      onClick={() => setValue('householdInvoice', true, { shouldValidate: true })}
                    />
                    <Chip
                      label='Nej'
                      color={!householdInvoice ? 'primary' : 'default'}
                      variant={!householdInvoice ? 'filled' : 'outlined'}
                      onClick={() => setValue('householdInvoice', false, { shouldValidate: true })}
                    />
                  </Box>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.75 }}>
                    {householdInvoice ? 'Hushållsmedlemmar samlas på en faktura till huvudmedlemmen.' : 'Varje medlem får en egen faktura.'}
                  </Typography>
                </Box>

                {/* Avancerat: smala inställningar som de flesta inte behöver röra. Hopfälld som standard. */}
                <Accordion
                  disableGutters
                  elevation={0}
                  square
                  sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      Avancerat
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <FormControlLabel
                      sx={{ alignItems: 'flex-start', m: 0 }}
                      control={
                        <Checkbox
                          size='small'
                          sx={{ pt: 0, pr: 1 }}
                          checked={printZeroInvoice}
                          onChange={(e) => setValue('printZeroInvoice', e.target.checked, { shouldValidate: true })}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            Skapa faktura även för avgiftsfria medlemmar
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            En makulerad 0-kr-faktura skapas för medlemmar utan avgift (t.ex. hedersmedlemmar) om de omfattas av utskicket. Utan detta
                            hoppas de över.
                          </Typography>
                        </Box>
                      }
                    />
                  </AccordionDetails>
                </Accordion>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minHeight: 40 }}>
                  <Typography variant='h4' component='div' sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {loading ? <CircularProgress size={22} /> : recipients ?? '–'}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {recipients === 1 ? 'faktura' : 'fakturor'}
                  </Typography>
                </Box>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                  {pre}
                  {membersCovered ?? '–'} medlemmar omfattas
                </Typography>

                {dotsTotal > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {Array.from({ length: dotsTotal }).map((_, i) => (
                      <Box
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        sx={{
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          bgcolor: i < selectedDots ? 'primary.main' : 'action.disabledBackground',
                          transition: 'background-color .2s',
                        }}
                      />
                    ))}
                  </Box>
                )}

                {perDot > 1 && (
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1.5 }}>
                    Varje prick motsvarar ca {perDot} medlemmar.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Grid>
      )}

      <SendingCopyFromMissionModal open={compareTarget !== null} variant={compareTarget ?? 'include'} onClose={() => setCompareTarget(null)} />
      <SendingCopySelectionModal open={copySelectionOpen} onClose={() => setCopySelectionOpen(false)} />

      {/* Förklaring till förfallodatum */}
      <Dialog open={explainOpen} onClose={() => setExplainOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Förfallodatum</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            Varje medlem har ett förfallodatum, även kallat ”Betalt medlemskap t.o.m.”. Ange den period som medlemmarnas förfallodatum ska ligga inom
            för att tas med.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant='contained' disableElevation onClick={() => setExplainOpen(false)}>
            Stäng
          </Button>
        </DialogActions>
      </Dialog>
    </GridWrapper>
  );
};
